import { toast } from 'react-hot-toast'
import initSqlJs from 'sql.js'
import { lessonService } from './lesson/LessonService'
import { API_CONFIG } from '../config/api.config'

// Initialize SQL.js
let SQL: any = null
const initSQL = async () => {
  if (!SQL) {
    SQL = await initSqlJs({
      locateFile: (file: string) => `https://sql.js.org/dist/${file}`
    })
  }
  return SQL
}

export interface BankQuestion {
  id: string
  type: 'trac_nghiem_nhieu_lua_chon' | 'dung_sai' | 'tinh_toan' | 'multiple_choice' | 'true_false' | 'short_answer'
  cau_hoi: string
  lua_chon?: string[]
  dap_an_dung: any
  loi_giai?: string // Tách riêng lời giải
  goi_y?: string[]
  muc_do: 'M1' | 'M2' | 'M3' // Mức độ thay vì độ khó
  diem: number
  hinh_anh?: string
  hinh_anh_giai_thich?: string
  // DS Schema fields
  question_lead?: string // Câu dẫn cho DS questions
  // Statement fields for True/False questions
  statement_a?: string
  statement_b?: string
  statement_c?: string
  statement_d?: string
  // Answer fields for True/False questions (Đúng/Sai)
  answer_a?: string
  answer_b?: string
  answer_c?: string
  answer_d?: string
  // Solution fields for True/False questions
  solution_a?: string
  solution_b?: string
  solution_c?: string
  solution_d?: string
  examMetadata?: {
    examType: 'GKI' | 'GKII' | 'CKI' | 'CKII'
    lop: number
    chuong: number
    bai: number
    group: 'TN' | 'DS' | 'TL' | 'TLU'
    difficulty: 'M1' | 'M2' | 'M3'
  }
}

export interface BankQuizData {
  id: string
  tieuDe: string
  moTa: string
  gioi_han_thoi_gian: number
  diem_dat: number
  cauHoi: BankQuestion[]
}

class BankQuestionService {
  /**
   * Cache câu hỏi theo chương để tránh load lại quá nhiều lần
   */
  private chapterQuestionCache: Map<string, BankQuestion[]> = new Map()
  private questionCountsCache: Map<string, any> = new Map()
  private dbCache: Map<string, BankQuestion[]> = new Map()

  /**
   * Store current teacher context để xử lý image paths đúng
   */
  private currentTeacherId?: string
  private currentLessonInfo: { lop: string; chuong: string; bai: string } | null = null

  /**
   * Tạo đường dẫn đến file questions.db trong thư mục bank
   */
  private getBankPath(lop: string, chuong: string, bai: string, loaiCauHoi: 'TN' | 'DS' | 'TL' | 'TLU'): string {
    // Format: L{lop}_C{chuong}_B{bai}_{loaiCauHoi}
    const lopFormatted = `L${lop}`
    const chuongFormatted = `C${chuong}`

    // Trích xuất số bài từ chuỗi bai (có thể là "1", "01", "1-dao-dong-dieu-hoa-...")
    let baiNumber = bai

    // Nếu bai có dạng "1-dao-dong-dieu-hoa-...", lấy số đầu tiên
    const match = bai.match(/^(\d+)/)
    if (match) {
      baiNumber = match[1]
    }

    const baiFormatted = `B${baiNumber.padStart(2, '0')}`

    // Ưu tiên dùng file .json vì dữ liệu mới hơn và nhẹ hơn
    const fileName = `questions_${loaiCauHoi.toLowerCase()}.json`
    const path = `${API_CONFIG.bank.baseUrl}/${lopFormatted}/${chuongFormatted}/${baiFormatted}/${loaiCauHoi}/${fileName}`
    console.log(`🔍 Bank path: ${path} (from "${bai}" -> "${baiNumber}")`)
    return path
  }

  /**
   * Lấy loại câu hỏi từ đường dẫn
   */
  private getQuestionTypeFromPath(dbPath: string): string {
    console.log(`🔍 getQuestionTypeFromPath: checking path "${dbPath}"`)
    if (dbPath.includes('/TN/')) {
      console.log('✅ Detected TN type')
      return 'tn'
    }
    if (dbPath.includes('/DS/')) {
      console.log('✅ Detected DS type')
      return 'ds'
    }
    if (dbPath.includes('/TL/')) {
      console.log('✅ Detected TL type')
      return 'tl'
    }
    if (dbPath.includes('/TLU/')) {
      console.log('✅ Detected TLU type')
      return 'tlu'
    }
    console.log('⚠️ No type detected, defaulting to TN')
    return 'tn' // default
  }

  /**
   * Load câu hỏi từ file .db hoặc .json
   */
  private async loadQuestionsFromDB(dbPath: string): Promise<BankQuestion[]> {
    // Check cache first
    if (this.dbCache.has(dbPath)) {
      console.log(`� Returning cached questions for: ${dbPath}`)
      return this.dbCache.get(dbPath)!
    }

    console.log(`�🔄 Starting to load questions from: ${dbPath}`)

    try {
      // ✅ REMOVED cache busting for speed
      const url = dbPath

      // Thử load file .json trước (ưu tiên)
      console.log(`📡 Fetching: ${url}`)
      const response = await fetch(url)
      console.log(`📡 Response status: ${response.status}, ok: ${response.ok}`)

      if (!response.ok) {
        console.log(`❌ JSON file not found: ${response.status} ${response.statusText}`)

        // Thử fallback sang file .db (nếu file json không tồn tại)
        const dbUrl = dbPath.replace('.json', '.db')
        console.log(`🔄 Trying DB format fallback: ${dbUrl}`)
        const dbResponse = await fetch(dbUrl)

        if (!dbResponse.ok) {
          console.log(`❌ DB file also not found: ${dbResponse.status}`)
          return []
        }

        console.log(`✅ Database file found (${dbResponse.headers.get('content-length')} bytes), loading with SQL.js...`)
        const questions = await this.loadFromSQLiteDB(dbResponse, dbUrl)

        // Cache result
        if (questions.length > 0) this.dbCache.set(dbPath, questions)
        return questions
      }

      // Parse JSON data
      const data = await response.json()
      // console.log(`✅ JSON data loaded:`, data) // Reduce log noise
      const rawQuestions = this.parseQuestionData(data)

      // Map raw questions to BankQuestion format
      const questions = rawQuestions.map(q => this.mapRawQuestionToBankQuestion(q, dbPath))

      // Cache result
      if (questions.length > 0) this.dbCache.set(dbPath, questions)
      return questions

    } catch (error) {
      console.error(`❌ Error loading questions from ${dbPath}:`, error)
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        console.error('⚠️ Possible CORS error or network issue. Check if the server allows CORS requests from this origin.')
      }
      return []
    }
  }

  /**
   * Load câu hỏi từ SQLite database data (Uint8Array) - dùng cho teacher databases
   */
  private async loadQuestionsFromSQLiteData(dbData: Uint8Array, questionType: 'TN' | 'DS' | 'TL' | 'TLU'): Promise<BankQuestion[]> {
    console.log(`🗄️ [loadQuestionsFromSQLiteData] START - ${questionType}, ${dbData.length} bytes`)

    // Khai báo db ở ngoài để có thể truy cập trong catch block
    let db: any = null

    try {
      // Validate data size
      if (!dbData || dbData.length === 0) {
        console.warn('⚠️ [loadQuestionsFromSQLiteData] Empty database data')
        return []
      }

      // Initialize SQL.js with timeout
      console.log(`🔧 [loadQuestionsFromSQLiteData] Initializing SQL.js...`)
      let sqlJS: any = null
      try {
        const initPromise = initSQL()
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('SQL.js initialization timeout')), 10000)
        )
        sqlJS = await Promise.race([initPromise, timeoutPromise]) as any
        console.log(`✅ [loadQuestionsFromSQLiteData] SQL.js initialized successfully`)
      } catch (initError: any) {
        console.error(`❌ [loadQuestionsFromSQLiteData] SQL.js init failed:`, initError)
        throw new Error(`SQL.js initialization failed: ${initError?.message || 'Unknown error'}`)
      }

      // Validate SQLite magic number
      console.log(`🔍 [loadQuestionsFromSQLiteData] Validating SQLite magic number...`)
      const magicBytes = dbData.slice(0, 16)
      const magicString = String.fromCharCode(...magicBytes.slice(0, 15))

      if (!magicString.startsWith('SQLite format 3')) {
        console.error(`❌ [loadQuestionsFromSQLiteData] File is not a valid SQLite database`)
        throw new Error('file is not a database')
      }

      console.log(`✅ [loadQuestionsFromSQLiteData] SQLite magic number validated`)

      try {
        console.log(`🔧 [loadQuestionsFromSQLiteData] Creating database from Uint8Array...`)
        db = new sqlJS.Database(dbData)
        console.log(`✅ [loadQuestionsFromSQLiteData] Database loaded successfully`)
      } catch (dbError: any) {
        console.error('❌ [loadQuestionsFromSQLiteData] Error creating database from Uint8Array:', dbError)
        throw new Error(`Database creation failed: ${dbError?.message || 'Unknown error'}`)
      }

      // Lấy danh sách bảng
      console.log(`🔍 [loadQuestionsFromSQLiteData] Getting table list...`)
      let tables: any = null
      try {
        tables = db.exec("SELECT name FROM sqlite_master WHERE type='table'")
        console.log('📊 [loadQuestionsFromSQLiteData] Database tables result:', tables?.length || 0)
      } catch (execError: any) {
        console.error('❌ [loadQuestionsFromSQLiteData] Error executing query:', execError)
        if (db) db.close()
        throw new Error(`Query execution failed: ${execError?.message || 'Unknown error'}`)
      }

      if (tables.length === 0 || !tables[0].values || tables[0].values.length === 0) {
        console.warn('⚠️ [loadQuestionsFromSQLiteData] No tables found in database')
        if (db) db.close()
        return []
      }

      const tableNames = tables[0].values.map(row => row[0] as string)
      console.log('📋 [loadQuestionsFromSQLiteData] Available tables:', tableNames)

      // Xác định tên bảng dựa trên loại câu hỏi
      const specificTableName = `questions_${questionType.toLowerCase()}`

      // Thử bảng cụ thể trước, sau đó thử các tên bảng phổ biến
      const possibleTableNames = [specificTableName, 'questions_tn', 'questions_ds', 'questions_tl', 'questions_tlu', 'questions', 'cau_hoi', 'question', 'quiz']
      console.log(`🔍 Trying table names in order:`, possibleTableNames)

      let questions: BankQuestion[] = []
      let foundTableName = ''

      for (const tableName of possibleTableNames) {
        try {
          console.log(`🔍 [loadQuestionsFromSQLiteData] Trying table: ${tableName}`)
          const result = db.exec(`SELECT * FROM ${tableName}`)

          if (result.length > 0 && result[0].values.length > 0) {
            console.log(`✅ [loadQuestionsFromSQLiteData] Found table '${tableName}' with ${result[0].values.length} rows`)
            console.log(`📋 [loadQuestionsFromSQLiteData] Columns:`, result[0].columns)
            foundTableName = tableName
            console.log(`🔄 [loadQuestionsFromSQLiteData] Parsing SQLite result...`)
            questions = this.parseSQLiteResult(result[0], `teacher_${questionType}`)
            console.log(`✅ [loadQuestionsFromSQLiteData] Parsed ${questions.length} questions`)
            break
          } else {
            console.log(`⚠️ [loadQuestionsFromSQLiteData] Table '${tableName}' exists but has no data`)
          }
        } catch (e: any) {
          console.log(`❌ [loadQuestionsFromSQLiteData] Table '${tableName}' not found or error:`, e.message)
          continue
        }
      }

      if (questions.length === 0) {
        console.warn(`⚠️ No questions found in any table. Available tables: ${tableNames.join(', ')}`)
      } else {
        console.log(`✅ Successfully loaded ${questions.length} questions from table '${foundTableName}'`)
      }

      // Always close database
      if (db) {
        try {
          db.close()
        } catch (closeError) {
          console.warn('⚠️ Error closing database:', closeError)
        }
      }

      return questions

    } catch (error) {
      console.error('❌ Error loading SQLite database from Uint8Array:', error)
      // Try to close database if it was opened
      if (db) {
        try {
          db.close()
        } catch (closeError) {
          // Ignore close errors
        }
      }
      return []
    }
  }

  /**
   * Load câu hỏi từ SQLite database
   */
  private async loadFromSQLiteDB(response: Response, dbPath: string): Promise<BankQuestion[]> {
    console.log(`🗄️ Loading SQLite database from: ${dbPath}`)

    try {
      // Initialize SQL.js
      console.log(`🔧 Initializing SQL.js...`)
      const sqlJS = await initSQL()
      console.log(`✅ SQL.js initialized successfully`)

      // Load database
      const arrayBuffer = await response.arrayBuffer()
      console.log(`📦 ArrayBuffer size: ${arrayBuffer.byteLength} bytes`)

      // ✅ Validate SQLite magic number trước khi load
      const uint8Array = new Uint8Array(arrayBuffer)

      // SQLite database bắt đầu với "SQLite format 3\000"
      const magicBytes = uint8Array.slice(0, 16)
      const magicString = String.fromCharCode(...magicBytes.slice(0, 15))

      if (!magicString.startsWith('SQLite format 3')) {
        // Kiểm tra xem có phải HTML error page không
        const textDecoder = new TextDecoder('utf-8', { fatal: false })
        const preview = textDecoder.decode(uint8Array.slice(0, 200))

        if (preview.includes('<!DOCTYPE') || preview.includes('<html') || preview.includes('404') || preview.includes('403')) {
          console.error(`❌ File is not a database - appears to be HTML error page: ${dbPath}`)
          console.error(`📄 Preview: ${preview.substring(0, 100)}...`)
        } else {
          console.error(`❌ File is not a valid SQLite database: ${dbPath}`)
          console.error(`📊 Magic bytes: ${Array.from(magicBytes.slice(0, 16)).map(b => b.toString(16).padStart(2, '0')).join(' ')}`)
        }
        throw new Error('file is not a database')
      }

      console.log(`✅ SQLite magic number validated`)

      const db = new sqlJS.Database(uint8Array)
      console.log(`✅ Database loaded successfully`)

      // Lấy danh sách bảng để hiểu cấu trúc database
      const tables = db.exec("SELECT name FROM sqlite_master WHERE type='table'")
      console.log('📊 Database tables result:', tables)

      if (tables.length === 0 || !tables[0].values || tables[0].values.length === 0) {
        console.warn('⚠️ No tables found in database')
        db.close()
        return []
      }

      const tableNames = tables[0].values.map(row => row[0] as string)
      console.log('📋 Available tables:', tableNames)

      // Xác định tên bảng dựa trên loại câu hỏi từ đường dẫn
      const questionType = this.getQuestionTypeFromPath(dbPath)
      console.log(`🎯 Question type from path: ${questionType}`)

      const specificTableName = `questions_${questionType.toLowerCase()}`

      // Thử bảng cụ thể trước, sau đó thử các tên bảng phổ biến
      const possibleTableNames = [specificTableName, 'questions_tn', 'questions_ds', 'questions_tl', 'questions_tlu', 'questions', 'cau_hoi', 'question', 'quiz']
      console.log(`🔍 Trying table names in order:`, possibleTableNames)

      let questions: BankQuestion[] = []
      let foundTableName = ''

      for (const tableName of possibleTableNames) {
        try {
          console.log(`🔍 Trying table: ${tableName}`)
          const result = db.exec(`SELECT * FROM ${tableName}`)

          if (result.length > 0 && result[0].values.length > 0) {
            console.log(`✅ Found table '${tableName}' with ${result[0].values.length} rows`)
            console.log(`📋 Columns:`, result[0].columns)
            foundTableName = tableName
            questions = this.parseSQLiteResult(result[0], dbPath)
            break
          } else {
            console.log(`⚠️ Table '${tableName}' exists but has no data`)
          }
        } catch (e) {
          console.log(`❌ Table '${tableName}' not found or error:`, e.message)
          continue
        }
      }

      if (questions.length === 0) {
        console.warn(`⚠️ No questions found in any table. Available tables: ${tableNames.join(', ')}`)
      } else {
        console.log(`✅ Successfully loaded ${questions.length} questions from table '${foundTableName}'`)
      }

      db.close()
      return questions

    } catch (error) {
      console.error('❌ Error loading SQLite database:', error)
      return []
    }
  }

  /**
   * Lấy danh sách câu hỏi theo chương và loại câu hỏi (TN, DS, TL)
   */
  async getQuestionsByChapterAndDifficulty(options: {
    lop: number
    chuong: number
    loai: 'TN' | 'DS' | 'TL' | 'TLU'
    mucDo?: 'M1' | 'M2' | 'M3'
  }): Promise<BankQuestion[]> {
    const { lop, chuong, loai, mucDo } = options
    const cacheKey = `L${lop}_C${chuong}_${loai}`

    try {
      let questions = this.chapterQuestionCache.get(cacheKey)

      if (!questions) {
        const lessons = await lessonService.filterLessons({ lop, chuong })
        if (!lessons.length) {
          console.warn(`⚠️ Không tìm thấy bài học cho L${lop} chương ${chuong}`)
          return []
        }

        const questionPromises = lessons.map(async lesson => {
          // Map TLU -> TLU for database path (FIX: Do not map to TL)
          const dbLoai = loai
          const dbPath = this.getBankPath(String(lop), String(chuong), String(lesson.bai), dbLoai)
          return await this.loadQuestionsFromDB(dbPath)
        })

        const results = await Promise.allSettled(questionPromises)
        const merged: BankQuestion[] = []

        results.forEach(result => {
          if (result.status === 'fulfilled' && Array.isArray(result.value)) {
            merged.push(...result.value)
          }
        })

        questions = this.deduplicateQuestions(merged)
        this.chapterQuestionCache.set(cacheKey, questions)
      }

      if (mucDo) {
        return questions.filter(q => q.muc_do === mucDo)
      }

      return questions
    } catch (error) {
      console.error(`❌ Lỗi khi lấy câu hỏi theo chương: L${lop} C${chuong} ${loai}`, error)
      return []
    }
  }

  /**
   * Xóa cache chương (dùng khi cần cập nhật câu hỏi mới)
   */
  clearChapterCache(): void {
    this.chapterQuestionCache.clear()
  }

  private deduplicateQuestions(questions: BankQuestion[]): BankQuestion[] {
    const map = new Map<string, BankQuestion>()

    questions.forEach(question => {
      if (!question || !question.id) return
      if (!map.has(question.id)) {
        map.set(question.id, question)
      }
    })

    return Array.from(map.values())
  }

  /**
   * Parse DS fields từ cau_hoi và lua_chon khi database không có DS fields riêng biệt
   */
  private parseDSFromCauHoiAndLuaChon(cauHoi: string, luaChon: string[], dapAnDung: any): any | null {
    try {
      console.log('🔍 Parsing DS from:', { cauHoi: cauHoi?.substring(0, 100), luaChon, dapAnDung })

      // Tách câu dẫn và các phát biểu từ cau_hoi
      let questionLead = cauHoi
      const patterns = [
        /Xét tính đúng[,\s]*sai của các phát biểu sau[:\s]*/i,
        /Xét tính đúng[,\s]*sai[:\s]*/i,
        /Các phát biểu sau[:\s]*/i
      ]

      for (const pattern of patterns) {
        const parts = cauHoi.split(pattern)
        if (parts.length >= 2) {
          questionLead = parts[0].trim()
          console.log('✅ Found question lead using pattern:', pattern)
          break
        }
      }

      // Nếu có lua_chon, sử dụng làm statements
      if (luaChon && luaChon.length >= 4) {
        console.log('✅ Using lua_chon as DS statements:', luaChon)

        // Parse đáp án đúng
        let answers = { a: false, b: false, c: false, d: false }

        if (typeof dapAnDung === 'string') {
          // Format như "ĐSSS" hoặc "TFFF" hoặc "1010"
          const answerStr = dapAnDung.toUpperCase()
          console.log('🔍 Parsing answer string:', answerStr)
          for (let i = 0; i < Math.min(4, answerStr.length); i++) {
            const char = answerStr[i]
            const key = String.fromCharCode(97 + i) // a, b, c, d
            answers[key] = char === 'Đ' || char === 'T' || char === '1' || char === 'TRUE'
          }
        } else if (Array.isArray(dapAnDung)) {
          // Array format
          for (let i = 0; i < Math.min(4, dapAnDung.length); i++) {
            const key = String.fromCharCode(97 + i)
            answers[key] = Boolean(dapAnDung[i])
          }
        }

        const result = {
          question_lead: questionLead,
          statement_a: luaChon[0] || '',
          statement_b: luaChon[1] || '',
          statement_c: luaChon[2] || '',
          statement_d: luaChon[3] || '',
          answer_a: answers.a,
          answer_b: answers.b,
          answer_c: answers.c,
          answer_d: answers.d
        }

        console.log('✅ Parsed DS result:', result)
        return result
      }

      console.log('⚠️ Cannot parse DS: insufficient lua_chon data')
      return null

    } catch (error) {
      console.error('❌ Error parsing DS from cau_hoi:', error)
      return null
    }
  }

  /**
   * Parse kết quả từ SQLite
   */
  private parseSQLiteResult(result: any, dbPath: string): BankQuestion[] {
    const { columns, values } = result
    const questions: BankQuestion[] = []

    console.log(`🔍 Parsing ${values.length} questions from database`)
    console.log(`📋 Available columns:`, columns)

    for (const row of values) {
      try {
        const question: any = {}

        // Map columns to values
        for (let i = 0; i < columns.length; i++) {
          question[columns[i]] = row[i]
        }

        // Use the centralized mapping function
        const bankQuestion = this.mapRawQuestionToBankQuestion(question, dbPath)

        // Debug: Log image fields
        if (bankQuestion.hinh_anh || bankQuestion.hinh_anh_giai_thich) {
          console.log(`🖼️ Question ${bankQuestion.id} has images:`, {
            question_image: bankQuestion.hinh_anh,
            explanation_image: bankQuestion.hinh_anh_giai_thich
          })
        }

        questions.push(bankQuestion)
      } catch (error) {
        console.error('Error processing question:', error, row)
        questions.push({
          id: `question_${Date.now()}_${Math.random()}`,
          type: 'multiple_choice',
          cau_hoi: 'Câu hỏi không xác định',
          lua_chon: [],
          dap_an_dung: null,
          loi_giai: '',
          goi_y: [],
          muc_do: 'M1',
          diem: 1,
          hinh_anh: undefined,
          hinh_anh_giai_thich: undefined
        })
      }
    }

    console.log(`✅ Parsed ${questions.length} questions successfully`)
    return questions
  }

  /**
   * Convert raw question object (from JSON or DB) to BankQuestion format
   */
  private mapRawQuestionToBankQuestion(question: any, dbPath: string): BankQuestion {
    // Xử lý câu hỏi - ưu tiên question_lead cho câu dẫn
    // Thêm các trường noi_dung, content, text, body để support nhiều format hơn
    const questionText = question.question_lead ||
      question.cau_hoi ||
      question.question ||
      question.QUESTION ||
      question.noi_dung ||
      question.content ||
      question.text ||
      question.body ||
      ''

    const { processedQuestion, processedOptions, correctAnswer } = this.processQuestionContent(
      questionText,
      this.parseLuaChonFromDB(question)
    )

    // Xử lý lời giải riêng biệt
    const solutionText = question.solution_text ||
      question.giai_thich ||
      question.explanation ||
      question.EXPLANATION ||
      question.loi_giai ||
      question.solution ||
      ''
    const processedSolution = this.processSolutionContent(solutionText)

    // Process image paths
    const questionImage = question.question_image_link ||
      question.hinh_anh ||
      question.image ||
      question.IMAGE ||
      question.Imagelink ||
      question.imagelink ||
      question.image_link ||
      question.ImageLink

    const explanationImage = question.solution_image_link ||
      question.hinh_anh_giai_thich ||
      question.explanation_image ||
      question.EXPLANATION_IMAGE ||
      question.SolutionImagelink ||
      question.solution_imagelink ||
      question.Solution_Imagelink

    // Xác định loại câu hỏi từ đường dẫn database
    const dbQuestionType = this.getQuestionTypeFromPath(dbPath)

    // Lấy đáp án đúng theo loại câu hỏi
    let correctAnswerFromDB = correctAnswer
    if (!correctAnswerFromDB) {
      if (dbQuestionType === 'tl') {
        // Câu TL: Lấy trực tiếp answer_value KHÔNG qua parseDapAn để giữ nguyên giá trị số
        const rawAnswer = question.answer_value || question.correct_answer || question.dap_an_dung || question.answer || question.ANSWER
        // Chỉ trim và parse thành number nếu là string số
        if (typeof rawAnswer === 'string') {
          const trimmed = rawAnswer.trim()
          const numValue = parseFloat(trimmed)
          // Giữ nguyên dạng string để phù hợp với type
          correctAnswerFromDB = isNaN(numValue) ? trimmed : numValue.toString()
        } else {
          correctAnswerFromDB = String(rawAnswer)
        }
      } else if (dbQuestionType === 'ds') {
        // Câu DS: xử lý đặc biệt cho đáp án đúng sai
        correctAnswerFromDB = this.parseTrueFalseAnswer(question)
      } else {
        // Câu TN: sử dụng correct_answer hoặc answer_key (qua parseDapAn để convert 1->A, 2->B...)
        correctAnswerFromDB = this.parseDapAn(question.correct_answer || question.answer_key || question.dap_an_dung || question.answer || question.ANSWER)
      }
    }

    // Map type từ đường dẫn database
    let questionType: BankQuestion['type'] = 'multiple_choice'
    if (dbQuestionType === 'ds') {
      questionType = 'dung_sai'
    } else if (dbQuestionType === 'tn') {
      questionType = 'trac_nghiem_nhieu_lua_chon'
    } else if (dbQuestionType === 'tl') {
      questionType = 'tinh_toan'
    } else {
      // Fallback to field-based detection
      questionType = this.mapQuestionType(question.question_type || question.type || question.loai || question.TYPE)
    }

    // Convert to BankQuestion format
    const bankQuestion: BankQuestion = {
      id: question.id || question.ID || `q_${Date.now()}_${Math.random()}`,
      type: questionType,
      cau_hoi: processedQuestion,
      lua_chon: processedOptions,
      dap_an_dung: correctAnswerFromDB,
      loi_giai: processedSolution, // Tách riêng lời giải
      goi_y: this.parseGoiY(question.goi_y || question.hints || question.HINTS),
      muc_do: this.mapMucDo(question.difficulty_level || question.do_kho || question.difficulty || question.DIFFICULTY || question.level),
      diem: parseFloat(question.diem || question.points || question.POINTS || '1'),

      // DS Schema: Preserve DS-specific fields
      ...(dbQuestionType === 'ds' && {
        question_lead: question.question_lead,
        statement_a: question.statement_a,
        statement_b: question.statement_b,
        statement_c: question.statement_c,
        statement_d: question.statement_d,
        answer_a: question.answer_a,
        answer_b: question.answer_b,
        answer_c: question.answer_c,
        answer_d: question.answer_d,
        solution_a: question.solution_a,
        solution_b: question.solution_b,
        solution_c: question.solution_c,
        solution_d: question.solution_d
      }),
      hinh_anh: this.processImagePath(questionImage),
      hinh_anh_giai_thich: this.processImagePath(explanationImage)
    }

    // Debug DS fields preservation
    if (dbQuestionType === 'ds') {
      // If DS fields are missing, try to parse from cau_hoi and lua_chon
      if (!(bankQuestion as any).statement_a && bankQuestion.cau_hoi && bankQuestion.lua_chon) {
        const parsedDS = this.parseDSFromCauHoiAndLuaChon(bankQuestion.cau_hoi, bankQuestion.lua_chon, bankQuestion.dap_an_dung)
        if (parsedDS) {
          Object.assign(bankQuestion, parsedDS)
        }
      }
    }

    return bankQuestion
  }

  /**
   * Parse dữ liệu câu hỏi từ JSON
   */
  private parseQuestionData(data: any): any[] {
    console.log(`✅ Questions loaded:`, data)

    // Xử lý dữ liệu tùy theo format
    if (Array.isArray(data)) {
      return data
    } else if (data.questions && Array.isArray(data.questions)) {
      return data.questions
    } else if (data.cauHoi && Array.isArray(data.cauHoi)) {
      return data.cauHoi
    } else if (data.questions_tn && Array.isArray(data.questions_tn)) {
      return data.questions_tn
    } else if (data.questions_ds && Array.isArray(data.questions_ds)) {
      return data.questions_ds
    } else if (data.questions_tl && Array.isArray(data.questions_tl)) {
      return data.questions_tl
    } else if (data.questions_tlu && Array.isArray(data.questions_tlu)) {
      return data.questions_tlu
    } else {
      // Try to find any array property that looks like questions
      const keys = Object.keys(data)
      for (const key of keys) {
        if (Array.isArray(data[key]) && data[key].length > 0 && (data[key][0].id || data[key][0].cau_hoi)) {
          console.log(`✅ Found questions in generic key: ${key}`)
          return data[key]
        }
      }

      console.warn(`⚠️ Unexpected data format:`, data)
      return []
    }
  }

  /**
   * Map loại câu hỏi dựa trên đường dẫn database
   */
  private mapQuestionType(type: any): BankQuestion['type'] {
    if (!type) {
      // Nếu không có type, xác định từ đường dẫn database
      return 'multiple_choice'
    }

    const typeStr = String(type).toLowerCase()
    if (typeStr.includes('tn') || typeStr.includes('trac_nghiem') || typeStr.includes('multiple')) return 'trac_nghiem_nhieu_lua_chon'
    if (typeStr.includes('ds') || typeStr.includes('dung_sai') || typeStr.includes('true_false')) return 'dung_sai'
    if (typeStr.includes('tl') || typeStr.includes('tinh_toan') || typeStr.includes('short')) return 'tinh_toan'

    return 'multiple_choice'
  }

  /**
   * Map mức độ (M1, M2, M3)
   */
  private mapMucDo(level: any): BankQuestion['muc_do'] {
    if (!level) return 'M1'

    const levelStr = String(level).toUpperCase()
    if (levelStr.includes('M1') || levelStr.includes('1') || levelStr.includes('EASY') || levelStr.includes('DE')) return 'M1'
    if (levelStr.includes('M2') || levelStr.includes('2') || levelStr.includes('MEDIUM') || levelStr.includes('TRUNG_BINH')) return 'M2'
    if (levelStr.includes('M3') || levelStr.includes('3') || levelStr.includes('HARD') || levelStr.includes('KHO')) return 'M3'

    return 'M1'
  }

  /**
   * Xử lý nội dung câu hỏi: nhận diện đáp án đúng có dấu *, biên dịch KaTeX
   */
  private processQuestionContent(questionText: string, options?: string[]): {
    processedQuestion: string
    processedOptions?: string[]
    correctAnswer?: string
  } {
    if (!questionText) return { processedQuestion: '' }

    // Biên dịch KaTeX trong câu hỏi
    let processedQuestion = this.compileKaTeX(questionText)

    let processedOptions = options
    let correctAnswer: string | undefined

    if (options && options.length > 0) {
      processedOptions = []

      for (let i = 0; i < options.length; i++) {
        let option = options[i]

        // Nhận diện đáp án đúng có dấu *
        if (option.includes('*')) {
          correctAnswer = String.fromCharCode(65 + i) // A, B, C, D
          option = option.replace(/\*/g, '').trim() // Bỏ dấu *
          console.log(`🎯 Found correct answer marker (*) in option ${i + 1}: "${option}" -> ${correctAnswer}`)
        }

        // Biên dịch KaTeX trong lựa chọn
        processedOptions.push(this.compileKaTeX(option))
      }
    }

    return {
      processedQuestion,
      processedOptions,
      correctAnswer
    }
  }

  /**
   * Xử lý lời giải: biên dịch KaTeX
   */
  private processSolutionContent(solutionText: string): string {
    if (!solutionText) return ''

    return this.compileKaTeX(solutionText)
  }

  /**
   * Prepare text for KaTeX rendering (keep original format for frontend processing)
   */
  private compileKaTeX(text: string): string {
    if (!text) return ''

    // Xử lý các công thức toán học trong $...$ hoặc $$...$$
    // Just return the original text - let KaTeXRenderer handle the actual compilation
    // Remove any * symbols that mark correct answers
    return text.replace(/\*/g, '').trim()
  }

  /**
   * Parse lựa chọn từ database schema mới
   */
  private parseLuaChonFromDB(question: any): string[] | undefined {
    // ✅ FIX: Ưu tiên các option riêng biệt (option_a, option_b, option_c, option_d)
    if (question.option_a || question.option_b || question.option_c || question.option_d) {
      const options: string[] = []
      // ✅ FIX: Thêm tất cả các option có giá trị (kể cả rỗng để giữ thứ tự)
      if (question.option_a !== null && question.option_a !== undefined) options.push(String(question.option_a).trim())
      if (question.option_b !== null && question.option_b !== undefined) options.push(String(question.option_b).trim())
      if (question.option_c !== null && question.option_c !== undefined) options.push(String(question.option_c).trim())
      if (question.option_d !== null && question.option_d !== undefined) options.push(String(question.option_d).trim())
      // ✅ FIX: Cũng thử option_e, option_f nếu có
      if (question.option_e !== null && question.option_e !== undefined) options.push(String(question.option_e).trim())
      if (question.option_f !== null && question.option_f !== undefined) options.push(String(question.option_f).trim())

      return options.length > 0 ? options.filter(opt => opt !== '') : undefined
    }

    // ✅ FIX: Thử các tên field khác nhau
    const luaChonField = question.lua_chon || question.options || question.OPTIONS || question.choices || question.CHOICES || question.luaChon
    if (luaChonField) {
      return this.parseLuaChon(luaChonField)
    }

    return undefined
  }

  /**
   * Parse lựa chọn
   */
  private parseLuaChon(luaChon: any): string[] | undefined {
    if (!luaChon) return undefined

    if (Array.isArray(luaChon)) return luaChon
    if (typeof luaChon === 'string') {
      try {
        return JSON.parse(luaChon)
      } catch {
        return luaChon.split('|').map(s => s.trim())
      }
    }

    return undefined
  }

  /**
   * Parse đáp án đúng sai cho câu hỏi DS theo cấu trúc DataModels.py
   */
  private parseTrueFalseAnswer(question: any): any {
    console.log(`🔍 Parsing True/False answer for question:`, question.id || 'unknown')

    // Ưu tiên 1: Sử dụng answer_a, answer_b, answer_c, answer_d (theo DataModels.py)
    const individualAnswers = {
      a: question.answer_a,
      b: question.answer_b,
      c: question.answer_c,
      d: question.answer_d
    }

    // Kiểm tra xem có đủ individual answers không
    const hasIndividualAnswers = Object.values(individualAnswers).some(answer =>
      answer !== null && answer !== undefined && answer !== ''
    )

    if (hasIndividualAnswers) {
      const result: boolean[] = []
      for (const key of ['a', 'b', 'c', 'd']) {
        const answer = individualAnswers[key as keyof typeof individualAnswers]
        if (answer) {
          const answerStr = String(answer).trim().toLowerCase()
          // Chuyển đổi "đúng"/"sai" thành boolean
          result.push(answerStr === 'đúng' || answerStr === 'true' || answerStr === '1')
        } else {
          result.push(false) // Default to false if no answer
        }
      }
      console.log(`✅ Parsed individual answers:`, individualAnswers, `-> [${result.join(', ')}]`)
      return result
    }

    // Ưu tiên 2: Fallback to legacy formats
    const answerFields = [
      'correct_answer', 'answer_key', 'dap_an_dung', 'answer', 'ANSWER'
    ]

    let answerData = null
    for (const field of answerFields) {
      if (question[field] !== null && question[field] !== undefined) {
        answerData = question[field]
        console.log(`✅ Found answer in field '${field}':`, answerData)
        break
      }
    }

    if (!answerData) {
      console.warn(`⚠️ No answer data found for DS question`)
      return [false, false, false, false] // Default all false
    }

    // Xử lý các format đáp án khác nhau
    if (typeof answerData === 'string') {
      const trimmed = answerData.trim().toUpperCase()

      // Format: "ĐSSS", "TFFF", "1010", "TTFF"
      if (trimmed.length >= 4) {
        const result: boolean[] = []
        for (let i = 0; i < Math.min(4, trimmed.length); i++) {
          const char = trimmed[i]
          result.push(char === 'Đ' || char === 'T' || char === '1')
        }
        console.log(`✅ Parsed string format: "${trimmed}" -> [${result.join(', ')}]`)
        return result
      }

      // Format JSON string: "[true, false, false, true]"
      try {
        const parsed = JSON.parse(answerData)
        if (Array.isArray(parsed)) {
          const result = parsed.map(Boolean)
          console.log(`✅ Parsed JSON array: ${answerData} -> [${result.join(', ')}]`)
          return result
        }
      } catch (e) {
        // Not JSON, continue
      }
    }

    // Nếu là array
    if (Array.isArray(answerData)) {
      const result = answerData.map(Boolean)
      console.log(`✅ Parsed array: [${answerData.join(', ')}] -> [${result.join(', ')}]`)
      return result
    }

    console.warn(`⚠️ Unknown answer format:`, answerData)
    return [false, false, false, false] // Default all false
  }

  /**
   * Parse đáp án đúng với logic cải thiện
   */
  private parseDapAn(dapAn: any): any {
    if (!dapAn) return null

    console.log(`🔍 Parsing correct answer:`, dapAn)

    // Nếu là string, xử lý đặc biệt
    if (typeof dapAn === 'string') {
      const trimmed = dapAn.trim()

      // Xử lý đáp án dạng "A", "B", "C", "D"
      if (/^[A-D]$/i.test(trimmed)) {
        console.log(`✅ Parsed letter answer: ${trimmed.toUpperCase()}`)
        return trimmed.toUpperCase()
      }

      // THAY ĐỔI THỨ TỰ: Kiểm tra index 0-3 TRƯỚC 1-4 để đảm bảo tính nhất quán với QuizScoringService
      // Xử lý đáp án dạng "0", "1", "2", "3" (index) - ƯU TIÊN CAO HƠN
      // Logic: hệ thống ưu tiên 0-based index. "0"->A, "1"->B
      if (/^[0-3]$/.test(trimmed)) {
        const letter = String.fromCharCode(65 + parseInt(trimmed)) // 0->A, 1->B, ...
        console.log(`✅ Parsed index answer (priority): ${trimmed} -> ${letter}`)
        return letter
      }

      // Xử lý đáp án dạng "1", "2", "3", "4" (ordinal) - ƯU TIÊN THẤP HƠN
      // Chỉ chạy vào đây nếu không khớp 0-3 (tức là trường hợp "4")
      // Hoặc nếu muốn hỗ trợ format 1-based, nhưng sẽ bị conflict ở 1,2,3.
      // Với việc đưa 0-3 lên trước, 1->B, 2->C, 3->D. Chỉ có 4->D.
      if (/^[1-4]$/.test(trimmed)) {
        const letter = String.fromCharCode(64 + parseInt(trimmed)) // 1->A, 2->B, ...
        console.log(`✅ Parsed numeric answer (ordinal): ${trimmed} -> ${letter}`)
        return letter
      }

      // Xử lý đáp án dạng "true", "false", "đúng", "sai"
      if (trimmed.toLowerCase() === 'true' || trimmed.toLowerCase() === 'đúng' || trimmed === '1') {
        console.log(`✅ Parsed boolean answer: ${trimmed} -> true`)
        return true
      }
      if (trimmed.toLowerCase() === 'false' || trimmed.toLowerCase() === 'sai' || trimmed === '0') {
        console.log(`✅ Parsed boolean answer: ${trimmed} -> false`)
        return false
      }

      // Xử lý đáp án dạng số thập phân
      const numValue = parseFloat(trimmed)
      if (!isNaN(numValue)) {
        console.log(`✅ Parsed numeric answer: ${trimmed} -> ${numValue}`)
        return numValue
      }

      // Giữ nguyên string nếu không match pattern nào
      console.log(`⚠️ Keeping original string answer: ${trimmed}`)
      return trimmed
    }

    // Nếu là number, giữ nguyên
    if (typeof dapAn === 'number') {
      console.log(`✅ Keeping numeric answer: ${dapAn}`)
      return dapAn
    }

    // Nếu là boolean, giữ nguyên
    if (typeof dapAn === 'boolean') {
      console.log(`✅ Keeping boolean answer: ${dapAn}`)
      return dapAn
    }

    // Nếu là array, xử lý từng phần tử
    if (Array.isArray(dapAn)) {
      console.log(`✅ Parsing array answer:`, dapAn)
      return dapAn.map(item => this.parseDapAn(item))
    }

    // Nếu là object, thử parse JSON
    if (typeof dapAn === 'object') {
      try {
        const jsonStr = JSON.stringify(dapAn)
        console.log(`✅ Parsed object answer:`, dapAn)
        return dapAn
      } catch (error) {
        console.warn(`⚠️ Failed to parse object answer:`, error)
        return dapAn
      }
    }

    console.log(`⚠️ Unknown answer type: ${typeof dapAn}, value:`, dapAn)
    return dapAn
  }

  /**
   * Parse gợi ý
   */
  private parseGoiY(goiY: any): string[] | undefined {
    if (!goiY) return undefined

    if (Array.isArray(goiY)) return goiY
    if (typeof goiY === 'string') {
      try {
        return JSON.parse(goiY)
      } catch {
        return goiY.split('|').map(s => s.trim())
      }
    }

    return undefined
  }

  /**
   * Trộn ngẫu nhiên mảng câu hỏi
   */
  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }

  /**
   * Load câu hỏi đúng sai từ database DS
   */
  async getTrueFalseQuestions(
    lop: string,
    chuong: string,
    bai: string
  ): Promise<BankQuestion[]> {
    console.log(`🎯 Loading True/False questions for L${lop}_C${chuong}_B${bai}`)

    try {
      const dsPath = this.getBankPath(lop, chuong, bai, 'DS')
      console.log(`📁 DS Database path: ${dsPath}`)

      const questions = await this.loadQuestionsFromDB(dsPath)
      console.log(`✅ Loaded ${questions.length} DS questions`)

      // Validate that questions have proper statement fields
      const validQuestions = questions.filter(q => {
        const hasStatements = q.statement_a || q.statement_b || q.statement_c || q.statement_d
        const hasLuaChon = q.lua_chon && q.lua_chon.length >= 4

        if (!hasStatements && !hasLuaChon) {
          console.warn(`⚠️ Question ${q.id} missing statement fields and lua_chon`)
          return false
        }

        return true
      })

      console.log(`✅ ${validQuestions.length} valid DS questions with proper statement data`)
      return validQuestions

    } catch (error) {
      console.error(`❌ Error loading DS questions:`, error)
      return []
    }
  }

  /**
   * Lấy câu hỏi ngẫu nhiên từ bank theo lớp, chương, bài
   * Tạo đúng 10 câu: 6 câu TN (trắc nghiệm nhiều lựa chọn), 2 câu ĐS (đúng/sai), 2 câu TL (trả lời ngắn)
   * Thời gian: 15 phút
   * Sắp xếp: 6 TN → 2 ĐS → 2 TL
   */
  async getRandomQuestions(
    lop: string,
    chuong: string,
    bai: string,
    options: {
      thoiGianLamBai?: number // Thời gian làm bài (phút)
      diemDat?: number        // Điểm đạt (%)
    } = {},
    teacherId?: string,
    teacherDatabases?: { TN?: Uint8Array | null; DS?: Uint8Array | null; TL?: Uint8Array | null }
  ): Promise<BankQuizData> {
    // Store teacherId và lesson info để dùng trong processImagePath
    this.currentTeacherId = teacherId
    this.currentLessonInfo = teacherId ? { lop, chuong, bai } : null
    const {
      thoiGianLamBai = 15, // Mặc định 15 phút
      diemDat = 70
    } = options

    console.log(`🎯 Creating quiz for L${lop}_C${chuong}_B${bai} with distribution: 6xTN, 2xDS, 2xTL (15 phút)`, teacherId ? `(Teacher: ${teacherId})` : '(System)')

    // Load câu hỏi từ từng loại
    let tnQuestions: BankQuestion[] = []
    let dsQuestions: BankQuestion[] = []
    let tlQuestions: BankQuestion[] = []

    // Load từ TN (Trắc nghiệm nhiều lựa chọn)
    try {
      if (teacherDatabases?.TN && teacherDatabases.TN.length > 0) {
        // Load từ teacher database
        console.log(`📚 Loading TN from teacher database (${teacherDatabases.TN.length} bytes)`)
        tnQuestions = await this.loadQuestionsFromSQLiteData(teacherDatabases.TN, 'TN')
        console.log(`✅ Loaded ${tnQuestions.length} TN questions from teacher database`)
      } else {
        // Load từ system
        const tnPath = this.getBankPath(lop, chuong, bai, 'TN')
        tnQuestions = await this.loadQuestionsFromDB(tnPath)
        console.log(`✅ Loaded ${tnQuestions.length} TN questions from system`)
      }
    } catch (error) {
      console.error(`❌ Error loading TN questions:`, error)
      // Fallback to system if teacher database fails
      try {
        const tnPath = this.getBankPath(lop, chuong, bai, 'TN')
        tnQuestions = await this.loadQuestionsFromDB(tnPath)
        console.log(`✅ Fallback: Loaded ${tnQuestions.length} TN questions from system`)
      } catch (fallbackError) {
        console.log(`⚠️ No TN questions found`)
      }
    }

    // Load từ DS (Đúng/Sai)
    try {
      if (teacherDatabases?.DS && teacherDatabases.DS.length > 0) {
        // Load từ teacher database
        console.log(`📚 Loading DS from teacher database (${teacherDatabases.DS.length} bytes)`)
        dsQuestions = await this.loadQuestionsFromSQLiteData(teacherDatabases.DS, 'DS')
        console.log(`✅ Loaded ${dsQuestions.length} DS questions from teacher database`)
      } else {
        // Load từ system
        const dsPath = this.getBankPath(lop, chuong, bai, 'DS')
        dsQuestions = await this.loadQuestionsFromDB(dsPath)
        console.log(`✅ Loaded ${dsQuestions.length} DS questions from system`)
      }
    } catch (error) {
      console.error(`❌ Error loading DS questions:`, error)
      // Fallback to system if teacher database fails
      try {
        const dsPath = this.getBankPath(lop, chuong, bai, 'DS')
        dsQuestions = await this.loadQuestionsFromDB(dsPath)
        console.log(`✅ Fallback: Loaded ${dsQuestions.length} DS questions from system`)
      } catch (fallbackError) {
        console.log(`⚠️ No DS questions found`)
      }
    }

    // Load từ TL (Tự luận/Trả lời ngắn)
    try {
      if (teacherDatabases?.TL && teacherDatabases.TL.length > 0) {
        // Load từ teacher database
        console.log(`📚 Loading TL from teacher database (${teacherDatabases.TL.length} bytes)`)
        tlQuestions = await this.loadQuestionsFromSQLiteData(teacherDatabases.TL, 'TL')
        console.log(`✅ Loaded ${tlQuestions.length} TL questions from teacher database`)
      } else {
        // Load từ system
        const tlPath = this.getBankPath(lop, chuong, bai, 'TL')
        tlQuestions = await this.loadQuestionsFromDB(tlPath)
        console.log(`✅ Loaded ${tlQuestions.length} TL questions from system`)
      }
    } catch (error) {
      console.error(`❌ Error loading TL questions:`, error)
      // Fallback to system if teacher database fails
      try {
        const tlPath = this.getBankPath(lop, chuong, bai, 'TL')
        tlQuestions = await this.loadQuestionsFromDB(tlPath)
        console.log(`✅ Fallback: Loaded ${tlQuestions.length} TL questions from system`)
      } catch (fallbackError) {
        console.log(`⚠️ No TL questions found`)
      }
    }

    console.log(`📊 Total questions loaded: TN=${tnQuestions.length}, DS=${dsQuestions.length}, TL=${tlQuestions.length}`)

    // Kiểm tra nếu không có câu hỏi
    if (tnQuestions.length === 0 && dsQuestions.length === 0 && tlQuestions.length === 0) {
      console.warn('⚠️ No questions found in database')
      return {
        id: `quiz_L${lop}_C${chuong}_B${bai}_${Date.now()}`,
        tieuDe: `Trắc nghiệm Lớp ${lop} - Chương ${chuong} - Bài ${bai}`,
        moTa: `Không có câu hỏi nào trong ngân hàng câu hỏi`,
        gioi_han_thoi_gian: thoiGianLamBai * 60,
        diem_dat: diemDat,
        cauHoi: []
      }
    }

    // Chọn câu hỏi ngẫu nhiên theo yêu cầu
    const selectedQuestions: BankQuestion[] = []

    // 1. Chọn 6 câu TN (trắc nghiệm nhiều lựa chọn)
    const shuffledTN = this.shuffleArray(tnQuestions)
    const selectedTN = shuffledTN.slice(0, Math.min(6, shuffledTN.length))
    selectedQuestions.push(...selectedTN)
    console.log(`✅ Selected ${selectedTN.length} TN questions`)

    // 2. Chọn 2 câu ĐS (đúng/sai)
    const shuffledDS = this.shuffleArray(dsQuestions)
    const selectedDS = shuffledDS.slice(0, Math.min(2, shuffledDS.length))
    selectedQuestions.push(...selectedDS)
    console.log(`✅ Selected ${selectedDS.length} DS questions`)

    // 3. Chọn 2 câu TL (trả lời ngắn)
    const shuffledTL = this.shuffleArray(tlQuestions)
    const selectedTL = shuffledTL.slice(0, Math.min(2, shuffledTL.length))
    selectedQuestions.push(...selectedTL)
    console.log(`✅ Selected ${selectedTL.length} TL questions`)

    // Nếu không đủ câu, bổ sung từ loại có nhiều câu nhất
    if (selectedQuestions.length < 10) {
      console.log(`⚠️ Not enough questions (${selectedQuestions.length}/10), adding from available pools...`)

      // Tính số câu còn thiếu
      const needed = 10 - selectedQuestions.length
      const allAvailable = [...tnQuestions, ...dsQuestions, ...tlQuestions].filter(
        q => !selectedQuestions.includes(q)
      )

      const shuffledAvailable = this.shuffleArray(allAvailable)
      const additional = shuffledAvailable.slice(0, needed)
      selectedQuestions.push(...additional)
      console.log(`✅ Added ${additional.length} additional questions`)
    }

    // Sắp xếp theo thứ tự: TN → ĐS → TL (KHÔNG trộn lại)
    const finalQuestions = selectedQuestions.slice(0, 10)

    // Tạo quiz data
    const quizData: BankQuizData = {
      id: `quiz_L${lop}_C${chuong}_B${bai}_${Date.now()}`,
      tieuDe: `Trắc nghiệm Lớp ${lop} - Chương ${chuong} - Bài ${bai}`,
      moTa: `Bài kiểm tra gồm ${finalQuestions.length} câu: ${selectedTN.length} TN, ${selectedDS.length} ĐS, ${selectedTL.length} TL`,
      gioi_han_thoi_gian: thoiGianLamBai * 60, // Convert to seconds (15 phút = 900 giây)
      diem_dat: diemDat,
      cauHoi: finalQuestions
    }

    console.log(`✅ Generated quiz with ${finalQuestions.length} questions (TN: ${selectedTN.length}, DS: ${selectedDS.length}, TL: ${selectedTL.length})`)

    if (finalQuestions.length === 0) {
      toast.error('Không tìm thấy câu hỏi nào trong ngân hàng câu hỏi cho bài học này')
    } else {
      toast.success(`Đã tạo bài kiểm tra với ${finalQuestions.length} câu (${selectedTN.length} TN, ${selectedDS.length} ĐS, ${selectedTL.length} TL)`)
    }

    return quizData
  }

  /**
   * Debug: Kiểm tra cấu trúc thư mục bank
   */
  async debugBankStructure(lop: string, chuong: string, bai: string): Promise<void> {
    console.log(`🔍 Debug bank structure for L${lop}_C${chuong}_B${bai}`)

    const types: ('TN' | 'DS' | 'TL')[] = ['TN', 'DS', 'TL']

    for (const type of types) {
      const path = this.getBankPath(lop, chuong, bai, type)
      console.log(`📁 Checking path: ${path}`)

      try {
        let response = await fetch(path)
        console.log(`📡 Response for ${type}: ${response.status} ${response.statusText}`)

        if (response.ok) {
          if (path.endsWith('.db')) {
            console.log(`✅ Database file found for ${type}`)
            // Thử đếm câu hỏi
            const count = await this.countQuestionsInDB(path)
            console.log(`📊 Question count for ${type}: ${count}`)
          } else {
            console.log(`✅ File found for ${type}`)
          }
        } else {
          console.log(`❌ File not found for ${type}`)
        }
      } catch (error) {
        console.error(`❌ Error checking ${type}:`, error)
      }
    }
  }

  /**
   * Đếm số câu hỏi có sẵn mà không cần load hết data
   */
  async getQuestionCounts(
    lop: string,
    chuong: string,
    bai: string,
    teacherId?: string | null,
    teacherDatabases?: Record<'TN' | 'DS' | 'TL', Uint8Array | null>
  ): Promise<{
    TN: number
    DS: number
    TL: number
    M1: number
    M2: number
    M3: number
  }> {
    const cacheKey = `counts_L${lop}_C${chuong}_B${bai}_${teacherId || 'system'}`

    // Check cache first (only if not using provided teacherDatabases which might change)
    if (!teacherDatabases && this.questionCountsCache.has(cacheKey)) {
      console.log(`📊 [Phần 4 - Thống kê] Returning cached counts for ${cacheKey}`)
      return this.questionCountsCache.get(cacheKey)
    }

    console.log(`📊 [Phần 4 - Thống kê] Getting question counts for L${lop}_C${chuong}_B${bai}`, teacherId ? `(Teacher: ${teacherId})` : '(System)')

    const counts = { TN: 0, DS: 0, TL: 0, M1: 0, M2: 0, M3: 0 }
    const types = ['TN', 'DS', 'TL'] as const

    // 1. Teacher Mode
    if (teacherId && typeof teacherId === 'string' && teacherId.trim().length > 0 && teacherDatabases) {
      console.log(`👨‍🏫 [Phần 4 - Thống kê] Teacher mode: teacherId=${teacherId}, teacherDatabases provided`)

      const promises = types.map(async (type) => {
        const dbData = teacherDatabases[type]
        if (dbData && dbData.length > 0) {
          try {
            // Timeout 5s
            const loadPromise = this.loadQuestionsFromSQLiteData(dbData, type)
            const timeoutPromise = new Promise<BankQuestion[]>((_, reject) =>
              setTimeout(() => reject(new Error(`Timeout loading ${type} database`)), 5000)
            )
            const questions = await Promise.race([loadPromise, timeoutPromise])
            return { type, questions }
          } catch (e) {
            console.error(`❌ Error loading teacher ${type}:`, e)
            return { type, questions: [] }
          }
        }
        return { type, questions: [] }
      })

      const results = await Promise.all(promises)
      let hasData = false

      results.forEach(({ type, questions }) => {
        if (questions.length > 0) {
          hasData = true
          counts[type] = questions.length
          counts.M1 += questions.filter(q => q.muc_do === 'M1').length
          counts.M2 += questions.filter(q => q.muc_do === 'M2').length
          counts.M3 += questions.filter(q => q.muc_do === 'M3').length
        }
      })

      if (hasData) {
        console.log(`✅ [Phần 4 - Thống kê] Loaded from teacher databases:`, counts)
        // Don't cache teacher databases results as they depend on the passed object
        return counts
      }
      console.warn('⚠️ [Phần 4 - Thống kê] No data from teacher databases, falling back to system')
    }

    // 2. System Mode (Parallelized)
    console.log('📊 [Phần 4 - Thống kê] Counting questions from SYSTEM database...')

    const systemPromises = types.map(async (type) => {
      try {
        const dbPath = this.getBankPath(lop, chuong, bai, type)
        // Use loadQuestionsFromDB to get both count and level info in one go
        // This avoids double fetching (once for count, once for levels) and runs in parallel
        const questions = await this.loadQuestionsFromDB(dbPath)
        return { type, questions }
      } catch (error) {
        console.error(`❌ Error loading system ${type}:`, error)
        return { type, questions: [] }
      }
    })

    const systemResults = await Promise.all(systemPromises)

    systemResults.forEach(({ type, questions }) => {
      counts[type] = questions.length
      counts.M1 += questions.filter(q => q.muc_do === 'M1').length
      counts.M2 += questions.filter(q => q.muc_do === 'M2').length
      counts.M3 += questions.filter(q => q.muc_do === 'M3').length
    })

    console.log(`📊 [Phần 4 - Thống kê] System final counts:`, counts)

    // Cache the result
    this.questionCountsCache.set(cacheKey, counts)

    return counts
  }

  /**
   * Đếm số câu hỏi trong database
   */
  private async countQuestionsInDB(dbPath: string): Promise<number> {
    console.log(`🔢 Counting questions in: ${dbPath}`)

    try {
      const response = await fetch(dbPath)
      console.log(`📡 Response: ${response.status} ${response.statusText}`)

      if (!response.ok) {
        console.log(`❌ Database file not accessible: ${response.status}`)

        // Thử file JSON
        const jsonPath = dbPath.replace('.db', '.json')
        console.log(`🔄 Trying JSON alternative: ${jsonPath}`)
        const jsonResponse = await fetch(jsonPath)

        if (!jsonResponse.ok) {
          console.log(`❌ JSON file also not accessible: ${jsonResponse.status}`)
          return 0
        }

        const data = await jsonResponse.json()
        const questions = this.parseQuestionData(data)
        console.log(`✅ Found ${questions.length} questions in JSON file`)
        return questions.length
      }

      // Nếu là file .db
      if (dbPath.endsWith('.db')) {
        console.log(`🗄️ Processing SQLite database (${response.headers.get('content-length')} bytes)...`)

        try {
          const sqlJS = await initSQL()
          const arrayBuffer = await response.arrayBuffer()
          const uint8Array = new Uint8Array(arrayBuffer)
          const db = new sqlJS.Database(uint8Array)

          // Get all tables first
          const tablesResult = db.exec("SELECT name FROM sqlite_master WHERE type='table'")
          const availableTables = tablesResult.length > 0 ?
            tablesResult[0].values.map(row => row[0] as string) : []

          console.log(`� AvTailable tables in database:`, availableTables)

          if (availableTables.length === 0) {
            console.warn(`⚠️ No tables found in database`)
            db.close()
            return 0
          }

          // Xác định tên bảng dựa trên loại câu hỏi từ đường dẫn
          const questionType = this.getQuestionTypeFromPath(dbPath)
          const specificTableName = `questions_${questionType.toLowerCase()}`

          // ✅ GIỮ NGUYÊN: Thử bảng cụ thể trước, sau đó thử các tên bảng phổ biến (logic ban đầu từ bankQuestionService_cu.ts)
          const possibleTableNames = [specificTableName, 'questions_tn', 'questions_ds', 'questions_tl', 'questions', 'cau_hoi', 'question', 'quiz']
          console.log(`🎯 Looking for tables in order:`, possibleTableNames)

          let count = 0
          let foundTable = ''

          // ✅ GIỮ NGUYÊN: Logic ban đầu - chỉ thử các table trong danh sách possibleTableNames nếu có trong availableTables
          // ✅ CẢI THIỆN: Thử query trực tiếp (case-insensitive) để tìm table đúng
          for (const tableName of possibleTableNames) {
            // ✅ FIX: Thử cả case-sensitive và case-insensitive matching
            const matchingTable = availableTables.find(t =>
              t.toLowerCase() === tableName.toLowerCase()
            ) || (availableTables.includes(tableName) ? tableName : null)

            if (matchingTable) {
              try {
                console.log(`🔍 Counting rows in table: ${matchingTable}`)
                const result = db.exec(`SELECT COUNT(*) as count FROM ${matchingTable}`)

                if (result.length > 0 && result[0].values.length > 0) {
                  count = result[0].values[0][0] as number
                  foundTable = matchingTable
                  console.log(`✅ Found ${count} questions in table '${matchingTable}'`)
                  break
                }
              } catch (e: any) {
                console.error(`❌ Error counting in table '${matchingTable}': ${e.message}`)
                continue
              }
            } else {
              console.log(`⚠️ Table '${tableName}' not available (case-sensitive check)`)
            }
          }

          // ✅ FIX: Nếu không tìm thấy trong danh sách expected, thử tất cả các table có sẵn (fallback)
          if (count === 0 && availableTables.length > 0) {
            console.log(`⚠️ No questions found in expected tables, trying all available tables as fallback...`)
            for (const tableName of availableTables) {
              try {
                console.log(`🔍 Counting rows in available table (fallback): ${tableName}`)
                const result = db.exec(`SELECT COUNT(*) as count FROM ${tableName}`)
                if (result.length > 0 && result[0].values.length > 0) {
                  const tableCount = result[0].values[0][0] as number
                  if (tableCount > count) {
                    count = tableCount
                    foundTable = tableName
                    console.log(`✅ Found ${count} questions in table '${tableName}' (fallback)`)
                  }
                }
              } catch (e: any) {
                console.log(`⚠️ Error counting in table '${tableName}': ${e.message}`)
                continue
              }
            }
          }

          if (count === 0) {
            console.warn(`⚠️ No questions found in any suitable table. Available: ${availableTables.join(', ')}`)
          } else {
            console.log(`✅ Final count: ${count} questions from table '${foundTable}'`)
          }

          db.close()
          return count

        } catch (sqlError) {
          console.error(`❌ SQL.js error:`, sqlError)
          return 0
        }
      }

      return 0
    } catch (error) {
      console.error(`❌ Network or processing error for ${dbPath}:`, error)
      return 0
    }
  }

  /**
   * Lấy tất cả câu hỏi có sẵn cho một bài học
   */
  async getAllAvailableQuestions(lop: string, chuong: string, bai: string): Promise<{
    TN: BankQuestion[]
    DS: BankQuestion[]
    TL: BankQuestion[]
  }> {
    console.log(`📊 Getting all available questions for L${lop}_C${chuong}_B${bai}`)

    const [tnQuestions, dsQuestions, tlQuestions] = await Promise.all([
      this.loadQuestionsFromDB(this.getBankPath(lop, chuong, bai, 'TN')),
      this.loadQuestionsFromDB(this.getBankPath(lop, chuong, bai, 'DS')),
      this.loadQuestionsFromDB(this.getBankPath(lop, chuong, bai, 'TL'))
    ])

    return {
      TN: tnQuestions,
      DS: dsQuestions,
      TL: tlQuestions
    }
  }

  /**
   * Xử lý đường dẫn ảnh từ database - sử dụng đường dẫn thực tế từ database
   * QUAN TRỌNG: Nếu load từ teacher database, convert bank/Images/... paths sang teacher paths
   */
  private processImagePath(imagePath: string | null | undefined): string | undefined {
    if (!imagePath || imagePath.trim() === '') {
      return undefined
    }

    const originalPath = decodeURIComponent(imagePath.trim())
    console.log(`🔍 Processing image path: ${originalPath}`)

    // Nếu đã là URL hợp lệ (http/https), kiểm tra và fix case nếu là link bank
    if (originalPath.startsWith('http://') || originalPath.startsWith('https://')) {
      if (originalPath.includes('vatli365.vn/bank/')) {
        // Fix case: images -> Images
        const fixedUrl = originalPath.replace(/\/images\//gi, '/Images/')
        console.log(`🌐 Fixed Bank URL: ${originalPath} -> ${fixedUrl}`)
        return fixedUrl
      }
      console.log(`🌐 Keeping URL as-is: ${originalPath}`)
      return originalPath
    }

    // ✅ QUAN TRỌNG: Nếu là teacher content path, kiểm tra và thêm L/C/B nếu thiếu
    if (originalPath.startsWith('/teacher-content/') || originalPath.startsWith('teacher-content/')) {
      const normalizedPath = originalPath.startsWith('/') ? originalPath : `/${originalPath}`

      // Kiểm tra xem path đã có cấu trúc L{lop}/C{chuong}/B{bai} chưa
      const hasStructuredPath = /\/L\d+\/C\d+\/B\d+\//.test(normalizedPath)

      if (!hasStructuredPath && this.currentTeacherId && this.currentLessonInfo) {
        // Path thiếu L/C/B, cần thêm vào
        const teacherIdMatch = normalizedPath.match(/\/teacher-content\/([^\/]+)/)
        const pathParts = normalizedPath.split('/').filter(p => p)
        const filename = pathParts.pop() || ''

        if (teacherIdMatch) {
          const teacherId = teacherIdMatch[1]
          const baiPadded = this.currentLessonInfo.bai.padStart(2, '0')
          const fixedPath = `/teacher-content/${teacherId}/L${this.currentLessonInfo.lop}/C${this.currentLessonInfo.chuong}/B${baiPadded}/${filename}`
          console.log(`🔧 [Quiz Image Path Fix] Thêm L/C/B: ${normalizedPath} → ${fixedPath}`)
          return fixedPath
        }
      }

      console.log(`👨‍🏫 Keeping teacher content path as-is: ${normalizedPath}`)
      return normalizedPath
    }

    // ✅ QUAN TRỌNG: Nếu đang load từ teacher database và path là bank/Images/..., convert sang teacher path
    if (this.currentTeacherId && this.currentLessonInfo) {
      const isBankImagePath = originalPath.toLowerCase().includes('bank/images/') || originalPath.toLowerCase().includes('bank\\images\\')

      if (isBankImagePath) {
        // Extract filename từ path
        const filename = originalPath.split('/').pop() || originalPath.split('\\').pop() || originalPath
        // Tạo teacher path với cấu trúc L{lop}/C{chuong}/B{bai}
        // Pad số bài với 0: 1 -> 01, 10 -> 10
        const baiPadded = this.currentLessonInfo.bai.padStart(2, '0')
        const teacherPath = `/teacher-content/${this.currentTeacherId}/L${this.currentLessonInfo.lop}/C${this.currentLessonInfo.chuong}/B${baiPadded}/${filename}`
        console.log(`👨‍🏫 Converting bank path to teacher path: ${originalPath} -> ${teacherPath}`)
        return teacherPath
      }
    }

    // Nếu đã là đường dẫn relative hợp lệ (bắt đầu với /), normalize về lowercase bank nhưng giữ Images
    if (originalPath.startsWith('/') && !originalPath.includes(':\\')) {
      // Normalize "Bank" -> "bank" để tương thích với cả Windows và Linux
      let normalizedPath = originalPath.replace(/\/Bank\//gi, '/bank/')
      // Normalize "images" -> "Images" (User confirmed server has Images)
      normalizedPath = normalizedPath.replace(/\/images\//gi, '/Images/')

      // Convert to full URL
      if (normalizedPath.startsWith('/bank/')) {
        return normalizedPath.replace('/bank', API_CONFIG.bank.baseUrl)
      }
      console.log(`📁 Normalized relative path: ${originalPath} -> ${normalizedPath}`)
      return normalizedPath
    }

    // Nếu đường dẫn bắt đầu với "bank/Images/" hoặc "Bank/Images/" (case-insensitive)
    if (originalPath.match(/^[Bb]ank\/[Ii]mages\//) || originalPath.match(/^[Bb]ank\\[Ii]mages\\/)) {
      // Normalize về "bank/Images/"
      // Xử lý cả dấu / và \
      const filename = originalPath.split(/[\\\/]/).pop() || ''
      const convertedPath = `/bank/Images/${filename}`
      const fullUrl = convertedPath.replace('/bank', API_CONFIG.bank.baseUrl)
      console.log(`📁 Converting bank path: ${originalPath} -> ${fullUrl}`)
      return fullUrl
    }

    // Xử lý đường dẫn tuyệt đối (Windows hoặc Unix) hoặc chỉ có tên file
    let fileName = ''
    let convertedPath = ''

    // Trích xuất tên file từ bất kỳ định dạng nào
    fileName = originalPath.split(/[\\\/]/).pop() || originalPath

    // Luôn đưa về thư mục bank/Images (Uppercase I as confirmed by user)
    convertedPath = `/bank/Images/${fileName}`
    const fullUrl = convertedPath.replace('/bank', API_CONFIG.bank.baseUrl)

    console.log(`🔄 Universal conversion to bank/Images: ${originalPath} -> ${fullUrl}`)
    return fullUrl
  }



  /**
   * Kiểm tra xem có câu hỏi nào available không
   */
  async hasQuestionsAvailable(lop: string, chuong: string, bai: string): Promise<boolean> {
    const available = await this.getAllAvailableQuestions(lop, chuong, bai)
    return available.TN.length > 0 || available.DS.length > 0 || available.TL.length > 0
  }

  /**
   * BỔ SUNG CHO EXAM MATRIX
   * Lấy số câu hỏi có sẵn theo chi tiết
   */
  async getAvailableQuestionCount(
    lop: number,
    chuong: number,
    bai: number,
    loai: 'TN' | 'DS' | 'TL' | 'TLU',
    mucDo: 'biet' | 'hieu' | 'vanDung'
  ): Promise<number> {
    try {
      console.log(`📊 Checking available questions: L${lop}_C${chuong}_B${bai}, ${loai}, ${mucDo}`)

      const dbPath = this.getBankPath(String(lop), String(chuong), String(bai), loai)
      const allQuestions = await this.loadQuestionsFromDB(dbPath)

      // Map mức độ: biet -> M1, hieu -> M2, vanDung -> M3
      const mucDoMap = {
        'biet': 'M1',
        'hieu': 'M2',
        'vanDung': 'M3'
      } as const

      const targetMucDo = mucDoMap[mucDo]
      const filtered = allQuestions.filter(q => q.muc_do === targetMucDo)

      console.log(`✅ Found ${filtered.length} questions`)
      return filtered.length

    } catch (error) {
      console.error(`❌ Error counting questions:`, error)
      return 0
    }
  }

  /**
   * Lấy câu hỏi theo yêu cầu chi tiết của ma trận
   */
  async getQuestionsByMatrixCell(
    lop: number,
    chuong: number,
    bai: number,
    loai: 'TN' | 'DS' | 'TL' | 'TLU',
    mucDo: 'biet' | 'hieu' | 'vanDung',
    count: number,
    dangToan?: string  // Để mở rộng sau
  ): Promise<BankQuestion[]> {
    try {
      console.log(`🎯 Getting questions: L${lop}_C${chuong}_B${bai}, ${loai}, ${mucDo}, count=${count}`)

      const dbPath = this.getBankPath(String(lop), String(chuong), String(bai), loai)
      const allQuestions = await this.loadQuestionsFromDB(dbPath)

      // Map mức độ
      const mucDoMap = {
        'biet': 'M1',
        'hieu': 'M2',
        'vanDung': 'M3'
      } as const

      const targetMucDo = mucDoMap[mucDo]

      // Filter theo mức độ
      let filtered = allQuestions.filter(q => q.muc_do === targetMucDo)

      // TODO: Filter theo dạng toán khi có field trong database
      // if (dangToan) {
      //   filtered = filtered.filter(q => q.dang_toan === dangToan)
      // }

      // Shuffle và lấy số câu cần thiết
      const shuffled = this.shuffleArray(filtered)
      const selected = shuffled.slice(0, Math.min(count, shuffled.length))

      console.log(`✅ Selected ${selected.length}/${count} questions`)
      return selected

    } catch (error) {
      console.error(`❌ Error getting questions:`, error)
      return []
    }
  }

  /**
   * Lấy tất cả số câu available cho một ma trận row
   */
  async getAvailableQuestionsForRow(
    lop: number,
    chuong: number,
    bai: number
  ): Promise<Record<string, number>> {
    console.log(`📊 Scanning available questions for L${lop}_C${chuong}_B${bai}`)

    const loaiCauHoi = ['TN', 'DS', 'TL', 'TLU'] as const
    const mucDo = ['biet', 'hieu', 'vanDung'] as const

    const result: Record<string, number> = {}

    // Scan song song tất cả các combination
    const promises: Promise<void>[] = []

    for (const loai of loaiCauHoi) {
      for (const muc of mucDo) {
        const key = `${loai}_${muc}`
        promises.push(
          this.getAvailableQuestionCount(lop, chuong, bai, loai, muc)
            .then(count => {
              result[key] = count
            })
        )
      }
    }

    await Promise.all(promises)

    console.log(`✅ Available questions:`, result)
    return result
  }
}

// Export class and singleton instance
export { BankQuestionService }
export const bankQuestionService = new BankQuestionService()
export default bankQuestionService